import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  SimpleSidebar,
  SimpleSidebarHeader,
} from "@volca/ui/components/ui/simple-sidebar";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { MemberChange } from "./utils";
import { formatRosterValue } from "./utils";

interface RosterChangesPanelProps {
  changes: MemberChange[];
  publishing: boolean;
  onClose: () => void;
  onPublish: () => void;
}

// The "preview": a push sidebar that opens beside the still-visible roster and
// shows exactly what each edit changes per tech — so the owner sees the impact
// on that person's payroll setup, side by side, before publishing.
export const RosterChangesPanel = ({
  changes,
  publishing,
  onClose,
  onPublish,
}: RosterChangesPanelProps) => {
  const editChanges = changes.filter(
    (change) => change.kind !== "removed" && change.kind !== "dismissed",
  );
  const removedChanges = changes.filter((change) => change.kind === "removed");
  const dismissedChanges = changes.filter(
    (change) => change.kind === "dismissed",
  );

  return (
    <SimpleSidebar aria-label="Review roster changes">
      <SimpleSidebarHeader
        className="px-6"
        title="Review roster changes"
        description={`${changes.length} ${changes.length === 1 ? "person" : "people"} will change in this new roster version.`}
        onClose={onClose}
      />

      <Stack align="start" gap={0} className="flex-1 overflow-y-auto p-6">
        <Stack className="w-full" align="start" gap={6}>
          {editChanges.length > 0 && (
            <Stack className="w-full" align="start" gap={4}>
              {editChanges.map((change) => (
                <Stack
                  align="start"
                  key={change.id}
                  gap={2}
                  className="w-full rounded-xl bg-neutral px-4 py-3"
                >
                  <Stack
                    className="w-full"
                    direction="row"
                    align="center"
                    justify="between"
                    gap={2}
                  >
                    <Stack direction="row" align="baseline" gap={2}>
                      <Text variant="label">{change.name || "New person"}</Text>
                      {change.employeeId && (
                        <Text variant="caption" tone="muted">
                          #{change.employeeId}
                        </Text>
                      )}
                    </Stack>
                    {change.kind === "added" && (
                      <Badge variant="secondary" color="success" size="sm">
                        New
                      </Badge>
                    )}
                  </Stack>
                  <Stack className="w-full" align="start" gap={2}>
                    {change.fields.map((field) => (
                      <Stack
                        key={field.key}
                        direction="row"
                        align="start"
                        justify="between"
                        gap={4}
                        className="w-full flex-wrap"
                      >
                        <Text
                          variant="caption"
                          tone="muted"
                          className="shrink-0 pt-px"
                        >
                          {field.label}
                        </Text>
                        <Stack
                          direction="row"
                          align="center"
                          justify="end"
                          gap={2}
                          className="flex-wrap"
                        >
                          {change.kind === "updated" && (
                            <Stack
                              direction="row"
                              align="center"
                              gap={1}
                              className="text-xs text-subtle"
                            >
                              <span className="line-through">
                                {formatRosterValue(field.before, field.field)}
                              </span>
                              <ArrowRightIcon className="size-3.5 shrink-0" />
                            </Stack>
                          )}
                          <span className="text-xs font-medium text-ink">
                            {formatRosterValue(field.after, field.field)}
                          </span>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          {removedChanges.length > 0 && (
            <Stack className="w-full" align="start" gap={2}>
              <Text
                variant="label"
                tone="muted"
                className="text-xs font-medium tracking-widest uppercase"
              >
                Will be removed
              </Text>
              {removedChanges.map((change) => (
                <Stack
                  align="start"
                  key={change.id}
                  gap={1}
                  className="w-full rounded-xl border border-danger-line bg-danger-subtle px-4 py-3"
                >
                  <Stack
                    className="w-full"
                    direction="row"
                    align="center"
                    justify="between"
                    gap={2}
                  >
                    <Stack direction="row" align="baseline" gap={2}>
                      <Text variant="label">
                        {change.name || "Unnamed person"}
                      </Text>
                      {change.employeeId && (
                        <Text variant="caption" tone="muted">
                          #{change.employeeId}
                        </Text>
                      )}
                    </Stack>
                    <Badge variant="secondary" color="danger" size="sm">
                      Removed
                    </Badge>
                  </Stack>
                  <Text variant="caption" className="text-danger">
                    Comes off payroll when this version publishes. They keep
                    their Volca account and their past pay.
                  </Text>
                </Stack>
              ))}
            </Stack>
          )}

          {dismissedChanges.length > 0 && (
            <Stack className="w-full" align="start" gap={2}>
              <Text
                variant="label"
                tone="muted"
                className="text-xs font-medium tracking-widest uppercase"
              >
                Will stop being suggested
              </Text>
              {dismissedChanges.map((change) => (
                <Stack
                  align="start"
                  key={change.id}
                  gap={1}
                  className="w-full rounded-xl bg-neutral px-4 py-3"
                >
                  <Stack direction="row" align="baseline" gap={2}>
                    <Text variant="label">
                      {change.name ||
                        (change.employeeId
                          ? `ServiceTitan employee #${change.employeeId}`
                          : "Dismissed person")}
                    </Text>
                    {change.name && change.employeeId && (
                      <Text variant="caption" tone="muted">
                        #{change.employeeId}
                      </Text>
                    )}
                  </Stack>
                  <Text variant="caption" tone="muted">
                    Marked as not on payroll, so the roster stops offering them.
                    They keep their Volca account so that they can see their
                    past payroll.
                  </Text>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack
        align="start"
        direction="row"
        justify="end"
        gap={2}
        className="w-full border-t p-6"
      >
        <Button variant="secondary" onClick={onClose}>
          Keep editing
        </Button>
        <Button
          disabled={publishing}
          aria-busy={publishing || undefined}
          onClick={onPublish}
        >
          {publishing && <Spinner data-icon="inline-start" aria-hidden />}
          Publish new version
        </Button>
      </Stack>
    </SimpleSidebar>
  );
};
